<template>
  <div class="momentum-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ΣP = m₁v₁ + m₂v₂ = 常数$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 物理场景 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="150" viewBox="0 0 400 150">
          <line x1="0" y1="130" x2="400" y2="130" stroke="rgba(255,255,255,0.15)" stroke-width="2" />
          
          <!-- 小车 1 -->
          <g :transform="`translate(${b1.x}, 130)`">
             <rect x="-20" y="-16" width="40" height="16" fill="#1e293b" rx="2" stroke="#3b82f6" stroke-width="1.5" />
             <text y="-22" text-anchor="middle" fill="#94a3b8" font-size="11" font-weight="700">{{ b1.m }}kg</text>
             <line x1="0" y1="-8" :x2="b1.v * 6" y2="-8" stroke="#3b82f6" stroke-width="2" marker-end="url(#arr-v)" v-if="Math.abs(b1.v) > 0.1" />
          </g>

          <!-- 小车 2 -->
          <g :transform="`translate(${b2.x}, 130)`">
             <rect x="-20" y="-16" width="40" height="16" fill="#1e293b" rx="2" stroke="#ef4444" stroke-width="1.5" />
             <text y="-22" text-anchor="middle" fill="#94a3b8" font-size="11" font-weight="700">{{ b2.m }}kg</text>
             <line x1="0" y1="-8" :x2="b2.v * 6" y2="-8" stroke="#ef4444" stroke-width="2" marker-end="url(#arr-v)" v-if="Math.abs(b2.v) > 0.1" />
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>P₁ (动量)</label>
              <span class="val">{{ b1.p.toFixed(1) }} <small>kg·m/s</small></span>
           </div>
           <div class="hud-item orange">
              <label>P₂ (动量)</label>
              <span class="val">{{ b2.p.toFixed(1) }} <small>kg·m/s</small></span>
           </div>
        </div>
      </div>

      <!-- 数据监测区 -->
      <div class="monitoring-dashboard">
         <div class="dash-item p-sum">
            <span class="label">系统总动量 ΣP:</span>
            <span class="val" :class="{ 'stable': Math.abs(currentTotalP - initialTotalP) < 0.1 }">{{ currentTotalP.toFixed(1) }} <small>kg·m/s</small></span>
         </div>
         <div class="momentum-bars">
            <div class="bar-row">
               <div class="bar-bg"><div class="bar-fill blue" :style="{ width: Math.min(100, Math.abs(b1.p)*4) + '%' }"></div></div>
               <span class="bar-label">P1</span>
            </div>
            <div class="bar-row">
               <div class="bar-bg"><div class="bar-fill red" :style="{ width: Math.min(100, Math.abs(b2.p)*4) + '%' }"></div></div>
               <span class="bar-label">P2</span>
            </div>
         </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>质量 m₁: {{ b1.m }}kg</label>
                 <el-slider v-model="b1.m" :min="1" :max="10" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>质量 m₂: {{ b2.m }}kg</label>
                 <el-slider v-model="b2.m" :min="1" :max="10" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>初速 v₁: {{ b1.v_init }}m/s</label>
                 <el-slider v-model="b1.v_init" :min="0" :max="20" size="small"></el-slider>
              </div>
              <div class="action-box">
                 <el-button type="primary" size="small" @click="runCollision" icon="el-icon-video-play" :disabled="runningAnimation">释放碰撞</el-button>
                 <el-button size="small" plain @click="reset" icon="el-icon-refresh-right">重置</el-button>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>无论碰撞多么剧烈，只要没有外力，系统内部球体相互作用后的<strong>总动量始终保持恒定</strong>。</p>
        </div>
      </div>
    </div>

    <defs>
      <marker id="arr-v" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"> <polygon points="0 0, 6 2, 0 4" fill="currentColor" /> </marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'MomentumConservationViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      b1: { x: 50, v: 5, v_init: 8, m: 2, p: 0 },
      b2: { x: 260, v: 0, v_init: 0, m: 4, p: 0 },
      collided: false,
      runningAnimation: false,
      frameId: null,
      running: true,
      initialTotalP: 0
    }
  },
  computed: {
    currentTotalP() { return this.b1.p + this.b2.p; }
  },
  methods: {
    runCollision() {
      if (this.frameId) cancelAnimationFrame(this.frameId);
      this.reset();
      this.initialTotalP = this.b1.m * this.b1.v_init;
      this.runningAnimation = true;
      this.animate();
    },
    reset() {
      this.runningAnimation = false;
      this.collided = false;
      this.b1.x = 50; this.b1.v = this.b1.v_init; this.b1.p = this.b1.m * this.b1.v;
      this.b2.x = 260; this.b2.v = 0; this.b2.p = 0;
      if (this.frameId) cancelAnimationFrame(this.frameId);
    },
    animate() {
      if (!this.running || !this.runningAnimation) {
        if (this.frameId) cancelAnimationFrame(this.frameId);
        this.frameId = null;
        return;
      }
      this.frameId = requestAnimationFrame(this.animate);
      
      this.b1.x += this.b1.v * 0.3;
      this.b2.x += this.b2.v * 0.3;
      
      this.b1.p = this.b1.m * this.b1.v;
      this.b2.p = this.b2.m * this.b2.v;

      // Collision detection
      if (Math.abs(this.b1.x - this.b2.x) < 40 && !this.collided) {
          this.collided = true;
          const m1 = this.b1.m, m2 = this.b2.m;
          const v1 = this.b1.v, v2 = this.b2.v;
          
          this.b1.v = ((m1 - m2) * v1 + 2 * m2 * v2) / (m1 + m2);
          this.b2.v = ((m2 - m1) * v2 + 2 * m1 * v1) / (m1 + m2);
          
          // Anti-overlap
          const offset = 40.1;
          if (this.b1.v > this.b2.v) this.b1.x = this.b2.x - offset;
          else this.b2.x = this.b1.x + offset;
      }

      if (this.b1.x > 450 || this.b2.x > 450 || this.b1.x < -100) {
          this.runningAnimation = false;
      }
    }
  },
  mounted() { this.running = true; this.reset(); },
  beforeDestroy() { 
    this.running = false;
    if (this.frameId) cancelAnimationFrame(this.frameId); 
  }
}
</script>

<style scoped lang="scss">
.momentum-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fdf2f8; color: #db2777; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 160px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f1f5f9; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.blue { border-left: 3px solid #3b82f6; } &.orange { border-left: 3px solid #f97316; } } } }
.monitoring-dashboard { background: #0f172a; border-radius: 12px; padding: 12px; border: 1px solid #1e293b; display: flex; flex-direction: column; gap: 10px; .dash-item { display: flex; justify-content: space-between; align-items: baseline; .label { font-size: 11px; color: #94a3b8; font-weight: 700; } .val { font-size: 16px; font-weight: 800; color: #ef4444; font-family: monospace; &.stable { color: #10b981; } small { font-size: 10px; opacity: 0.7; } } } .momentum-bars { display: flex; flex-direction: column; gap: 6px; .bar-row { display: flex; align-items: center; gap: 10px; .bar-bg { flex: 1; height: 6px; background: rgba(255,255,255,0.05); border-radius: 3px; overflow: hidden; .bar-fill { height: 100%; transition: width 0.1s; &.blue { background: #3b82f6; box-shadow: 0 0 10px rgba(59,130,246,0.3); } &.red { background: #ef4444; box-shadow: 0 0 10px rgba(239,68,68,0.3); } } } .bar-label { min-width: 25px; font-size: 10px; color: #64748b; font-weight: 800; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: 2px; } } .action-box { grid-column: span 2; display: flex; gap: 8px; border-top: 1px solid #f1f5f9; padding-top: 8px; .el-button { flex: 1; font-weight: 700; } } }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #bbf7d0; display: flex; align-items: center; gap: 10px; i { color: #16a34a; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #166534; line-height: 1.5; strong { color: #14532d; } } }
</style>
